import { buildBreadcrumbLd, stripHtml, truncate } from '~/shared/seo'
import type { DetailTabSeo } from '~/shared/seo'

interface UseDetailSeoOptions {
  tabs: DetailTabSeo[]
  /** '/schools' или '/trainers' */
  basePath: string
  /** 'Школы' или 'Тренеры' */
  listLabel: string
  /** 'спортивная школа' или 'тренер' */
  entityLabel: string
  name: () => string | undefined
  description: () => string | null | undefined
  image: () => string | null | undefined
  entityLd: () => object | undefined
}

export function useDetailSeo(options: UseDetailSeoOptions) {
  const route = useRoute()
  const config = useRuntimeConfig()

  const siteUrl = computed(() =>
    String(config.public.siteUrl || '').replace(/\/$/, ''),
  )

  const id = computed(() => route.params.id as string | undefined)

  const activeTab = computed(() => {
    const segments = route.path.replace(/\/$/, '').split('/')
    const last = segments[segments.length - 1]
    if (!id.value) return 'index'
    if (last === id.value) return 'index'
    return options.tabs.some((t) => t.value === last) ? last : 'index'
  })

  const activeTabMeta = computed(() =>
    options.tabs.find((t) => t.value === activeTab.value),
  )

  const isIndex = computed(() => activeTab.value === 'index')

  const title = computed(() => {
    const name = options.name()
    if (!name) {
      const label = options.entityLabel
      return `${label.charAt(0).toUpperCase()}${label.slice(1)}`
    }
    if (isIndex.value) return `${name} — ${options.entityLabel}`
    const tabLabel = activeTabMeta.value?.label.toLowerCase()
    return tabLabel ? `${name} — ${tabLabel}` : name
  })

  const description = computed(() => {
    const name = options.name() ?? ''
    if (isIndex.value) {
      const text = stripHtml(options.description())
      if (text) return truncate(text, 160)
      return `«${name}»: направления, тренеры, расписание и запись на занятия.`
    }
    const tpl = activeTabMeta.value?.description
    if (tpl) return tpl.replace('{name}', name)
    return `${activeTabMeta.value?.label ?? ''} ${options.entityLabel} «${name}».`
  })

  const noindex = computed(() => activeTabMeta.value?.noindex ?? false)

  const jsonLd = computed(() => {
    const name = options.name()
    if (!name || !id.value) return undefined
    const detailUrl = `${siteUrl.value}${options.basePath}/${id.value}`
    const crumbs = [
      { name: 'Главная', url: `${siteUrl.value}/` },
      { name: options.listLabel, url: `${siteUrl.value}${options.basePath}` },
    ]
    if (isIndex.value) {
      crumbs.push({ name, url: detailUrl })
      const entity = options.entityLd()
      return entity
        ? [entity, buildBreadcrumbLd(crumbs)]
        : [buildBreadcrumbLd(crumbs)]
    }
    crumbs.push({ name, url: detailUrl })
    crumbs.push({
      name: activeTabMeta.value?.label ?? '',
      url: `${siteUrl.value}${route.path}`,
    })
    return [buildBreadcrumbLd(crumbs)]
  })

  useSeo({
    title,
    description,
    image: () => options.image(),
    canonicalPath: () => route.path,
    noindex,
    jsonLd,
  })
}
